import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Spinner,
  ModalFooter,
  Text,
  Modal,
  Button,
} from '@chakra-ui/react'
import { FC, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { SSOAuthenticationModalProps } from '.'
import { ssoClientConfigMappings } from '../../features/sso/constant'
import ClientSSOContext, { SSO_PROVIDER_TYPE } from '../../features/sso/types/ClientSSOContext'
import { RootState } from '../../app/store'

const SSOOIDCAuthenticationModal: FC<SSOAuthenticationModalProps & { clientName?: string }> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  clientName,
}) => {
  const popupRef = useRef<Window | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const { email } = useSelector((state: RootState) => state.authentication)

  const getSSOAuthUrl = (): string | null => {
    const clientSSOContextDataString = localStorage.getItem('clientSSOContext')
    if (!clientSSOContextDataString) {
      return null
    }

    const clientSSOContextData: ClientSSOContext = JSON.parse(clientSSOContextDataString)
    const client = ssoClientConfigMappings.find(
      (config) => config.clientName === clientSSOContextData.clientName
    )

    if (!client?.clientId || !client?.identityProvider) {
      return null
    }

    const baseUrl = `${process.env.REACT_APP_COGNITO_AUTH_URI}/authorize`
    const redirectUri = `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${client.clientId}`
    const params = new URLSearchParams({
      response_type: 'code',
      identity_provider: client.identityProvider,
      client_id: client.clientId,
      redirect_uri: redirectUri,
      state: 'popup_auth=true',
    })
    if (clientSSOContextData.providerType === SSO_PROVIDER_TYPE.OIDC) {
      params.append('prompt', 'login')
      params.append('max_age', '0')
      params.append('login_hint', '')
      params.append('ui_locales', 'en')
    }

    return `${baseUrl}?${params.toString()}`
  }

  const openSSOPopup = () => {
    const authUrl = getSSOAuthUrl()
    if (!authUrl) {
      console.error('Unable to construct SSO auth URL')
      return
    }

    const currentUserSub = localStorage.getItem('user_id')
    if (currentUserSub) {
      localStorage.setItem('expected_user_sub', currentUserSub)
    }

    const popup = window.open(
      authUrl,
      'sso-auth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    )

    if (!popup) {
      console.error('Popup blocked by browser')
      return
    }

    popupRef.current = popup

    intervalRef.current = setInterval(() => {
      if (popup.closed) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        popupRef.current = null
        localStorage.removeItem('expected_user_sub')
        onClose()
      }
    }, 1000)

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SSO_AUTH_SUCCESS') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        if (popupRef.current) {
          popupRef.current.close()
          popupRef.current = null
        }
        localStorage.removeItem('expected_user_sub')
        window.removeEventListener('message', handleMessage)
        onSuccess()
      } else if (event.data?.type === 'SSO_AUTH_ERROR') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        if (popupRef.current) {
          popupRef.current.close()
          popupRef.current = null
        }
        localStorage.removeItem('expected_user_sub')
        window.removeEventListener('message', handleMessage)
        if (onError && event.data?.error) {
          onError(event.data.error)
        }
        onClose()
      }
    }

    window.addEventListener('message', handleMessage)
  }

  useEffect(() => {
    if (isOpen) {
      openSSOPopup()
    }

    return () => {
      if (popupRef.current) {
        popupRef.current.close()
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isOpen])

  const handleClose = () => {
    if (popupRef.current) {
      popupRef.current.close()
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>SSO Authentication</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Text>A new window will open for you to authenticate with your SSO provider.</Text>
            <Text fontSize="sm" color="gray.600">
              Client: {clientName}
            </Text>
            {email && (
              <VStack spacing={1}>
                <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                  Please use this email address:
                </Text>
                <Text fontSize="sm" fontFamily="mono" bg="gray.100" px={2} py={1} borderRadius="md">
                  {email}
                </Text>
              </VStack>
            )}
            <Spinner size="lg" />
            <Text fontSize="sm">Please complete authentication in the popup window...</Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SSOOIDCAuthenticationModal
